
























































import Vue from 'vue';
import {
  Component, Inject, Watch, Prop,
} from 'vue-property-decorator';
import { DataTableHeader } from 'vuetify';
import { mapState } from 'vuex';
import { Alert, NoticeClass, AuthStore } from '@kasasa/fbase-components/lib';
import {
  TransactionDetailsResource,
  TransactionDetails,
} from '@/api/transactiondetails';
import { Account } from '@/api/consumeraccountdetails';

interface TransactionHistoryTableEntry {
  postedDate: number | null;
  description: string;
  amount: string;
  transactionCode: string;
}

@Component({
  computed: mapState(['fiId']),
})
export default class TransactionHistorySheet extends Vue {
  @Prop({ default: false }) value!: boolean;

  @Prop() cycleId!: string;

  @Prop() transactionTypes!: string[];

  @Prop() account!: Account;

  @Prop() relationship!: string;

  @Prop({ default: 0 }) expectedEntryCount!: number;

  show: boolean = false;

  fiId!: string;

  loading: boolean = true;

  friendlyTypeName: string = '';

  entries: TransactionHistoryTableEntry[] = [];

  @Inject('TransactionDetailsResource')
  transactionDetailsResource!: TransactionDetailsResource;

  static readonly friendlyNameMap = new Map<string, string>([
    ['POS', 'POS'],
    ['ACH_CREDIT', 'ACH Credit'],
    ['ACH_DEBIT', 'ACH Debit'],
    ['BILL_PAY', 'ACH Debit'],
  ]);


  headers: DataTableHeader[] = [
    { text: 'Posted Date', value: 'postedDate' },
    { text: 'Description', value: 'description' },
    { text: 'Amount', value: 'amount' },
    { text: 'Transaction Code', value: 'transactionCode' },
  ];

  mounted() {
    this.show = this.value;
    this.setFriendlyTypeName();
    if (this.show) {
      this.loadData();
    }
  }

  @Watch('transactionTypes')
  setFriendlyTypeName() {
    this.friendlyTypeName = '';
    for (let i = 0; i < this.transactionTypes.length; i++) {
      const name: string | undefined = TransactionHistorySheet.friendlyNameMap.get(this.transactionTypes[i]);
      this.friendlyTypeName += name || 'Unknown';

      if (i < this.transactionTypes.length - 2) {
        this.friendlyTypeName += ', ';
      } else if (i === this.transactionTypes.length - 2) {
        this.friendlyTypeName += ' or ';
      }
    }
  }

  @Watch('value')
  onNewValue() {
    this.show = this.value;
    if (this.show) {
      this.loadData();
    } else {
      // Clear out the table when closed so that when re-opened we're scrolled to the top
      this.entries = [];
    }
  }

  onShowToggle(newValue: boolean) {
    this.show = newValue;
    this.$emit('input', this.show);
  }

  loadData() {
    this.loading = true;
    this.entries = [];

    // Fill with dummy values so that we can set the height of the card right away and it doesn't
    // "jump" when real data comes in.
    for (let i = 0; i < this.expectedEntryCount; i++) {
      this.entries.push({
        postedDate: null,
        description: '',
        amount: '',
        transactionCode: '',
      });
    }

    this.entries = [];
    this.requestData();
  }

  async requestData() {
    const promises: Promise<TransactionDetails[]>[] = [];

    this.transactionTypes.forEach((transactionType: string) => {
      promises.push(this.transactionDetailsResource
        .getByFiIdAndCycleIdAndTransactionType(
          this.fiId,
          this.cycleId,
          this.account.accountKasasaKey,
          transactionType,
        ));
    });

    await Promise.all(promises).then(
      (details: TransactionDetails[][]) => {
        details.forEach((detailsByType: TransactionDetails[]) => {
          detailsByType.forEach((detail: TransactionDetails) => {
            this.entries.push({
              postedDate: new Date(detail.postingDate).getTime(),
              description: detail.description,
              amount: `$${detail.amount}`,
              transactionCode: detail.transactionCode,
            });
          });
        });

        // Sort by posting date
        this.entries = this.entries.sort((a: TransactionHistoryTableEntry, b: TransactionHistoryTableEntry): number => {
          const aTime: number = a.postedDate ? a.postedDate : 0;
          const bTime: number = b.postedDate ? b.postedDate : 0;
          return bTime - aTime;
        });
      },
    ).catch((error: any) => {
      this.showErrorMessage(error);
    }).finally(() => {
      this.loading = false;
    });
  }

  showErrorMessage(error: string) {
    const al = new Alert(error, NoticeClass.ERROR);
    al.setTimeout(5000);
    this.$store.dispatch('notices/add', al);
  }
}
